import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { usePagePrefs } from "@pepsico/pref-permissions";

import {
  OrthoApprovalStatus,
  TaskStatus,
  type OrthoStatusResponse,
  type TaskResponse,
} from "api/model";
import {
  getGetOrthosQueryKey,
  getGetOrthoStatusQueryOptions,
  type getOrthoStatusResponse,
} from "api/orthos/orthos";
import { getGetRowSnipsQueryKey } from "api/snips/snips";
import { getGetAllTasksQueryKey, useGetAllTasks } from "api/tasks/tasks";
import { isStitchedOrthoStatus, useCogOrtho } from "context/CogOrtho";
import { loadOrthoMeta as loadOrthoHeaderMeta } from "context/CogOrtho/loadOrthoMeta";
import type { ImageCornerCoordinates } from "context/CogOrtho/orthoAssets";

import {
  analyzeOrthoImages,
  createLatestFramePublisher,
  createOrthoUploadSession,
  filesToOrthoImages,
  isInFlightSession,
  isJpegEntry,
  isZipFile,
  type OrthoFolderQcResult,
  type OrthoUploadSession,
  type OrthoUploadStatus,
} from "./orthoUpload";
import {
  buildOrthoWorkflows,
  getWorkflowKey,
  type ClientErrorsByKey,
  type ExistingOrthoMeta,
  type OrthoWorkflow,
  type OrthoWorkflowKey,
  type WorkflowError,
  type WorkflowStepName,
} from "./orthoWorkflow";
import {
  OrthoWorkflowContext,
  type OrthoWorkflowContextValue,
} from "./orthoWorkflowContext";
import { openOrthoZip } from "./orthoZip";
import {
  analyzeCanopyTasksParams,
  analyzeEmergenceTasksParams,
  hasInFlightTask,
  orthoCreationTasksParams,
  rawImageUploadTasksParams,
  selectLocationTasks,
  selectOrthoPlotTasks,
  snipOrthoTasksParams,
  TASK_POLL_INTERVAL_MS,
} from "./orthoWorkflowQueries";
import { getPlotTaskOrthoId, getPlotTaskTrialId } from "./orthoPlotTasks";
import { useOrthoUploader } from "./useOrthoUploader";
import { useOrthoWorkflowActions } from "./useOrthoWorkflowActions";

const selectOrthoStatus = (
  res: getOrthoStatusResponse,
): OrthoStatusResponse | null => (res.status === 200 ? res.data : null);

/** Module-level so `useQueries` only re-runs it when a status changes. */
const combineLiveStatus = (
  results: { data: OrthoStatusResponse | null | undefined }[],
): Record<number, OrthoStatusResponse> => {
  const byTaskId: Record<number, OrthoStatusResponse> = {};
  for (const { data } of results) {
    if (data != null) {
      byTaskId[data.task_id] = data;
    }
  }
  return byTaskId;
};

/**
 * Calls `onCompleted` with tasks that have newly reached `completed` since the
 * last render. Tasks already completed on the first load, or on the first load
 * after `locationUuid` changes, are not reported.
 */
const useOnTasksCompleted = (
  locationUuid: string,
  tasks: readonly TaskResponse[],
  isLoaded: boolean,
  onCompleted: (tasks: TaskResponse[]) => void,
) => {
  const statusByIdRef = useRef<{
    locationUuid: string;
    statusById: Map<number, TaskStatus>;
  } | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    const previousEntry = statusByIdRef.current;
    const previous =
      previousEntry?.locationUuid === locationUuid
        ? previousEntry.statusById
        : null;
    statusByIdRef.current = {
      locationUuid,
      statusById: new Map(tasks.map((task) => [task.id, task.status])),
    };
    if (previous == null) {
      return;
    }
    const newlyCompleted = tasks.filter(
      (task) =>
        task.status === TaskStatus.completed &&
        previous.get(task.id) !== TaskStatus.completed,
    );
    if (newlyCompleted.length > 0) {
      onCompleted(newlyCompleted);
    }
  }, [isLoaded, locationUuid, onCompleted, tasks]);
};

interface OrthoWorkflowProviderProps {
  children: ReactNode;
}

/**
 * Tracks each flight from folder upload through ortho creation and approval as an
 * `OrthoWorkflow`. Workflows are derived from the query cache each render; the
 * only state kept here is upload sessions, tab-only errors, and ortho metadata.
 *
 * Must be inside `CogOrthoProvider` (it reads the location's orthos).
 */
const OrthoWorkflowProvider: React.FC<OrthoWorkflowProviderProps> = ({
  children,
}) => {
  const [prefs] = usePagePrefs();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const queryClient = useQueryClient();
  const { orthos, presignedUrls } = useCogOrtho();

  // --- Existing ortho metadata (COG header extraction cache) ---
  const [knownOrthoMeta, setKnownOrthoMeta] = useState<
    Record<number, ExistingOrthoMeta>
  >({});
  // Per-ortho header reads, so concurrent callers share one COG open.
  const orthoMetaLoadsRef = useRef(new Map<number, Promise<void>>());

  // --- Tab-only errors, per workflow ---
  const [clientErrors, setClientErrors] = useState<ClientErrorsByKey>({});

  useEffect(() => {
    orthoMetaLoadsRef.current.clear();
    setKnownOrthoMeta({});
    setClientErrors({});
  }, [locationUuid]);

  /** One tab-only error per workflow, step, and (for analyze) trial; a new one replaces the old. */
  const reportError = useCallback(
    (
      key: OrthoWorkflowKey,
      {
        step,
        message,
        trialId,
      }: { step: WorkflowStepName; message: string; trialId?: number },
    ) => {
      const error: WorkflowError = {
        at: new Date().toISOString(),
        message,
        source: "client",
        step,
        ...(trialId != null && { trialId }),
      };
      setClientErrors((current) => ({
        ...current,
        [key]: [
          ...(current[key] ?? []).filter(
            (existing) =>
              existing.step !== step || existing.trialId !== trialId,
          ),
          error,
        ],
      }));
    },
    [],
  );

  const clearError = useCallback(
    (key: OrthoWorkflowKey, step: WorkflowStepName, trialId?: number) => {
      setClientErrors((current) => {
        const existing = current[key];
        const remaining = existing?.filter(
          (error) => error.step !== step || error.trialId !== trialId,
        );
        if (
          existing == null ||
          remaining == null ||
          remaining.length === existing.length
        ) {
          return current;
        }
        return { ...current, [key]: remaining };
      });
    },
    [],
  );

  const rememberOrthoBoundary = useCallback(
    (orthoId: number, coordinates: ImageCornerCoordinates) => {
      setKnownOrthoMeta((current) => {
        if (current[orthoId]?.boundary != null) {
          return current;
        }
        return {
          ...current,
          [orthoId]: { ...current[orthoId], boundary: coordinates },
        };
      });
    },
    [],
  );

  const loadOrthoMeta = useCallback((orthoId: number, url: string) => {
    const loads = orthoMetaLoadsRef.current;
    const inFlightOrDone = loads.get(orthoId);
    if (inFlightOrDone != null) {
      return inFlightOrDone;
    }

    const load = loadOrthoHeaderMeta(url).then(
      ({ boundary, cmPerPx }) => {
        setKnownOrthoMeta((current) => ({
          ...current,
          [orthoId]: {
            ...current[orthoId],
            // Keep an earlier footprint (e.g. from a map overlay) over a re-read.
            boundary: current[orthoId]?.boundary ?? boundary,
            cmPerPx,
          },
        }));
      },
      (error: unknown) => {
        loads.delete(orthoId);
        console.error(
          "[OrthoWorkflowProvider] Failed to load ortho metadata:",
          JSON.stringify(
            error instanceof Error
              ? { message: error.message, name: error.name, orthoId }
              : { error, orthoId },
          ),
        );
      },
    );
    loads.set(orthoId, load);
    return load;
  }, []);

  // Read each stitched ortho's header once, so cm/px and footprints are known
  // wherever the workflow is shown. Uploading and processing orthos have no COG yet.
  useEffect(() => {
    for (const ortho of orthos) {
      if (!isStitchedOrthoStatus(ortho.status)) {
        continue;
      }
      const url = presignedUrls.get(ortho.id);
      if (url != null) {
        void loadOrthoMeta(ortho.id, url);
      }
    }
  }, [loadOrthoMeta, orthos, presignedUrls]);

  // --- Upload sessions ---
  const [orthoUploadSessions, setOrthoUploadSessions] = useState<
    OrthoUploadSession[]
  >([]);
  const [activeOrthoUploadSessionId, setActiveOrthoUploadSessionId] = useState<
    string | null
  >(null);
  const activeOrthoUploadSession =
    orthoUploadSessions.find(
      (orthoUploadSession) =>
        orthoUploadSession.id === activeOrthoUploadSessionId,
    ) ?? null;
  const isAnyUploadInProgress = orthoUploadSessions.some(isInFlightSession);
  const byKeyRef = useRef<ReadonlyMap<OrthoWorkflowKey, OrthoWorkflow>>(
    new Map(),
  );

  const patchSession = useCallback(
    (id: string, patch: Partial<OrthoUploadSession>) => {
      const stampedPatch =
        patch.errorMessage != null
          ? { ...patch, errorAt: new Date().toISOString() }
          : patch;
      setOrthoUploadSessions((current) =>
        current.map((orthoUploadSession) =>
          orthoUploadSession.id === id
            ? { ...orthoUploadSession, ...stampedPatch }
            : orthoUploadSession,
        ),
      );
    },
    [],
  );
  const patchActiveSession = useCallback(
    (patch: Partial<OrthoUploadSession>) => {
      if (activeOrthoUploadSessionId != null) {
        patchSession(activeOrthoUploadSessionId, patch);
      }
    },
    [activeOrthoUploadSessionId, patchSession],
  );

  const onOrthoCreationError = useCallback(
    (orthoId: number) => {
      reportError(getWorkflowKey({ orthoId }), {
        message: "Failed to start ortho creation",
        step: "ortho_creation",
      });
    },
    [reportError],
  );

  const {
    addFiles: addUppyFiles,
    upload: uploadFiles,
    resumeUploads,
  } = useOrthoUploader(patchSession, onOrthoCreationError);

  // --- Server state ---
  const isLocationSelected = locationUuid !== "";

  const { data: uploadTasks, isPending: isUploadTasksPending } = useGetAllTasks(
    rawImageUploadTasksParams,
    {
      query: {
        enabled: isLocationSelected,
        select: (res) => selectLocationTasks(res, locationUuid),
      },
    },
  );

  const hasProcessingOrtho = orthos.some(
    (ortho) => ortho.status === OrthoApprovalStatus.processing,
  );
  const { data: creationTasks, isPending: isCreationTasksPending } =
    useGetAllTasks(orthoCreationTasksParams, {
      query: {
        enabled: isLocationSelected,
        // Poll while a flight is queued or being created, or its ortho is
        // already marked processing, so the stage advances on its own.
        refetchInterval: (query) =>
          hasProcessingOrtho ||
          hasInFlightTask(selectLocationTasks(query.state.data, locationUuid))
            ? TASK_POLL_INTERVAL_MS
            : false,
        select: (res) => selectLocationTasks(res, locationUuid),
      },
    });

  const orthoIds = useMemo(
    () => new Set(orthos.map((ortho) => ortho.id)),
    [orthos],
  );
  const { data: analyzeEmergenceTasks } = useGetAllTasks(
    analyzeEmergenceTasksParams,
    {
      query: {
        enabled: isLocationSelected,
        // Poll while an emergence analysis is queued or running, so its progress and result show up on their own.
        refetchInterval: (query) =>
          hasInFlightTask(selectOrthoPlotTasks(query.state.data, orthoIds))
            ? TASK_POLL_INTERVAL_MS
            : false,
        select: (res) => selectOrthoPlotTasks(res, orthoIds),
      },
    },
  );

  const { data: analyzeCanopyTasks } = useGetAllTasks(
    analyzeCanopyTasksParams,
    {
      query: {
        enabled: isLocationSelected,
        // Poll while a canopy analysis is queued or running, so its progress and result show up on their own.
        refetchInterval: (query) =>
          hasInFlightTask(selectOrthoPlotTasks(query.state.data, orthoIds))
            ? TASK_POLL_INTERVAL_MS
            : false,
        select: (res) => selectOrthoPlotTasks(res, orthoIds),
      },
    },
  );

  const { data: snipOrthoTasks } = useGetAllTasks(snipOrthoTasksParams, {
    query: {
      enabled: isLocationSelected,
      // Poll while plots are being snipped, so the analyses waiting on it show its progress.
      refetchInterval: (query) =>
        hasInFlightTask(selectOrthoPlotTasks(query.state.data, orthoIds))
          ? TASK_POLL_INTERVAL_MS
          : false,
      select: (res) => selectOrthoPlotTasks(res, orthoIds),
    },
  });

  const processingCreationTaskIds = (creationTasks ?? [])
    .filter((task) => task.status === TaskStatus.processing)
    .map((task) => task.id);
  const liveStatus = useQueries({
    queries: processingCreationTaskIds.map((taskId) =>
      getGetOrthoStatusQueryOptions(taskId, undefined, {
        query: {
          refetchInterval: TASK_POLL_INTERVAL_MS,
          select: selectOrthoStatus,
        },
      }),
    ),
    combine: combineLiveStatus,
  });

  // --- Side effects that used to depend on which items were mounted ---

  // ScaleODM reports done before the creation task does; refetch both.
  const finishedOrthoCreationTaskIds = Object.values(liveStatus)
    .filter((status) => status.progress === 100)
    .map((status) => status.task_id)
    .join(",");
  useEffect(() => {
    if (finishedOrthoCreationTaskIds === "") {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: getGetAllTasksQueryKey() });
    void queryClient.invalidateQueries({ queryKey: getGetOrthosQueryKey() });
  }, [finishedOrthoCreationTaskIds, queryClient]);

  const onCreationTasksCompleted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: getGetOrthosQueryKey() });
  }, [queryClient]);
  useOnTasksCompleted(
    locationUuid,
    creationTasks ?? [],
    creationTasks != null,
    onCreationTasksCompleted,
  );

  // The analysis results change when an analyze task completes.
  const onPlotTasksCompleted = useCallback(
    (completed: TaskResponse[]) => {
      for (const task of completed) {
        const orthoId = getPlotTaskOrthoId(task);
        const trialId = getPlotTaskTrialId(task);
        if (orthoId != null && trialId != null) {
          void queryClient.invalidateQueries({
            queryKey: getGetRowSnipsQueryKey({
              ortho_id: orthoId,
              trial_id: trialId,
            }),
          });
        }
      }
    },
    [queryClient],
  );
  useOnTasksCompleted(
    locationUuid,
    analyzeEmergenceTasks ?? [],
    analyzeEmergenceTasks != null,
    onPlotTasksCompleted,
  );
  useOnTasksCompleted(
    locationUuid,
    analyzeCanopyTasks ?? [],
    analyzeCanopyTasks != null,
    onPlotTasksCompleted,
  );

  useEffect(() => {
    if (activeOrthoUploadSession?.status === "uploading") {
      void queryClient.invalidateQueries({
        queryKey: getGetAllTasksQueryKey(rawImageUploadTasksParams),
      });
    }
  }, [activeOrthoUploadSession?.status, queryClient]);

  // --- Workflows ---

  const { actions, isActionPending, startingPlotTasks } =
    useOrthoWorkflowActions({
      byKeyRef,
      clearError,
      reportError,
    });

  // Memoized on the actual inputs so consumers see a stable workflow identity
  // across unrelated re-renders.
  const workflows = useMemo(
    () =>
      buildOrthoWorkflows({
        analyzeCanopyTasks,
        analyzeEmergenceTasks,
        clientErrors,
        creationTasks: creationTasks ?? [],
        liveStatus,
        orthoMeta: knownOrthoMeta,
        orthos,
        sessions: orthoUploadSessions,
        snipOrthoTasks,
        startingPlotTasks,
        uploadTasks: uploadTasks ?? [],
      }),
    [
      analyzeCanopyTasks,
      analyzeEmergenceTasks,
      clientErrors,
      creationTasks,
      knownOrthoMeta,
      liveStatus,
      orthoUploadSessions,
      orthos,
      snipOrthoTasks,
      startingPlotTasks,
      uploadTasks,
    ],
  );
  const byKey = useMemo(
    () => new Map(workflows.map((workflow) => [workflow.key, workflow])),
    [workflows],
  );

  useEffect(() => {
    byKeyRef.current = byKey;
  }, [byKey]);

  // --- Upload actions (act on the dialog's active session) ---

  const setFlightDateTime = useCallback(
    (flightDateTime: Date | null) => patchActiveSession({ flightDateTime }),
    [patchActiveSession],
  );
  const setSelectedSiteId = useCallback(
    (siteId: number | null) => patchActiveSession({ selectedSiteId: siteId }),
    [patchActiveSession],
  );
  /** Sets/clears the active session's `errorMessage`, for errors that don't go through `analyzeFolder`/`upload`. */
  const setOrthoErrorMessage = useCallback(
    (message: string | null) => patchActiveSession({ errorMessage: message }),
    [patchActiveSession],
  );
  const setUploadStatus = useCallback(
    (status: OrthoUploadStatus) => patchActiveSession({ status }),
    [patchActiveSession],
  );

  /** Starts a fresh session for a new upload, makes it active, and resets the shared Uppy instance. */
  const startNewSession = useCallback(
    (preset?: { orthoId: number; siteId: number }) => {
      const orthoUploadSession = createOrthoUploadSession(
        preset != null
          ? { orthoId: preset.orthoId, selectedSiteId: preset.siteId }
          : undefined,
      );
      setOrthoUploadSessions((current) => [
        ...current.filter(isInFlightSession),
        orthoUploadSession,
      ]);
      setActiveOrthoUploadSessionId(orthoUploadSession.id);
      addUppyFiles(orthoUploadSession.id, []);
    },
    [addUppyFiles],
  );

  /** Starts a session pre-populated with `tasks` to resume, and makes it active. */
  const startResumeSession = useCallback((resumeTasks: TaskResponse[]) => {
    const orthoUploadSession = createOrthoUploadSession({ resumeTasks });
    setOrthoUploadSessions((current) => [
      ...current.filter(isInFlightSession),
      orthoUploadSession,
    ]);
    setActiveOrthoUploadSessionId(orthoUploadSession.id);
  }, []);

  /** Deactivates the active session, discarding it if it never got past idle/error. */
  const clearActiveSession = useCallback(() => {
    setOrthoUploadSessions((current) => {
      const orthoUploadSession = current.find(
        (s) => s.id === activeOrthoUploadSessionId,
      );
      if (
        orthoUploadSession != null &&
        (orthoUploadSession.status === "idle" ||
          orthoUploadSession.status === "error")
      ) {
        return current.filter((s) => s.id !== activeOrthoUploadSessionId);
      }
      return current;
    });
    setActiveOrthoUploadSessionId(null);
  }, [activeOrthoUploadSessionId]);

  /** Selects `selectedFiles` for the session and runs `analyze` over them, publishing QC progress as it goes. */
  const runOrthoQcAnalysis = useCallback(
    async (
      sessionId: string,
      selectedFiles: File[],
      analyze: (
        onProgress: (partial: OrthoFolderQcResult) => void,
      ) => Promise<OrthoFolderQcResult>,
    ) => {
      patchSession(sessionId, {
        selectedFiles,
        errorMessage: null,
        qcResult: null,
        flightDateTime: null,
        isAnalyzing: true,
      });

      const qcProgressPublisher = createLatestFramePublisher(
        (partial: OrthoFolderQcResult) => {
          patchSession(sessionId, { qcResult: partial });
        },
      );

      try {
        const result = await analyze(qcProgressPublisher.schedule);
        // The last schedule() only queued a rAF; it may not have published yet.
        // Cancel so that leftover frame cannot patch qcResult again after this
        // final write (extra render, and it can land after isAnalyzing is cleared).
        // The unpublished snapshot is safe to drop: `result` already includes it.
        qcProgressPublisher.cancel();
        patchSession(sessionId, {
          qcResult: result,
          // Earliest capture in the set, including its clock time.
          flightDateTime: result.flightStart,
        });
      } catch (error) {
        // Same leftover-frame race as above, but after an error patch.
        qcProgressPublisher.cancel();
        const message =
          error instanceof Error ? error.message : "Failed to analyze folder";
        patchSession(sessionId, { errorMessage: message });
      } finally {
        patchSession(sessionId, { isAnalyzing: false });
      }
    },
    [patchSession],
  );

  /**
   * Analyzes a single zip by reading EXIF straight out of its entries, so the
   * images are never fully extracted. The zip itself is what gets uploaded.
   */
  const openAndAnalyzeZip = useCallback(
    async (sessionId: string, zipFile: File) => {
      patchSession(sessionId, { errorMessage: null, isAnalyzing: true });

      try {
        const archive = await openOrthoZip(zipFile);
        try {
          if (archive.imageCount === 0) {
            patchSession(sessionId, {
              errorMessage: "No JPEG files found in zip",
              isAnalyzing: false,
            });
            return;
          }
          await runOrthoQcAnalysis(sessionId, [zipFile], (onProgress) =>
            analyzeOrthoImages(
              archive.images(),
              archive.imageCount,
              onProgress,
            ),
          );
        } finally {
          await archive.close();
        }
      } catch (error) {
        patchSession(sessionId, {
          errorMessage:
            error instanceof Error ? error.message : "Failed to read zip file",
          isAnalyzing: false,
        });
      }
    },
    [patchSession, runOrthoQcAnalysis],
  );

  /**  Grab basic metrics from the ortho folder image files */
  const analyzeOrthoFolder = useCallback(
    async (files: File[]) => {
      const sessionId = activeOrthoUploadSessionId;
      if (sessionId == null) {
        return;
      }

      if (files.length === 1 && isZipFile(files[0])) {
        await openAndAnalyzeZip(sessionId, files[0]);
        return;
      }

      const jpegFiles = files.filter((file) =>
        isJpegEntry(file.webkitRelativePath || file.name),
      );

      if (jpegFiles.length === 0) {
        patchSession(sessionId, {
          errorMessage: "No JPEG files found in folder",
        });
        return;
      }

      await runOrthoQcAnalysis(sessionId, jpegFiles, (onProgress) =>
        analyzeOrthoImages(
          filesToOrthoImages(jpegFiles),
          jpegFiles.length,
          onProgress,
        ),
      );
    },
    [
      activeOrthoUploadSessionId,
      openAndAnalyzeZip,
      patchSession,
      runOrthoQcAnalysis,
    ],
  );

  /** Uploads the active session's currently selected/analyzed ortho folder to the current location. */
  const uploadOrthoFolderFile = useCallback(async () => {
    const orthoUploadSession = activeOrthoUploadSession;
    if (orthoUploadSession?.selectedFiles == null) {
      return;
    }
    const sessionId = orthoUploadSession.id;

    if (locationUuid === "") {
      patchSession(sessionId, {
        errorMessage: "Select a location before uploading",
      });
      return;
    }

    if (orthoUploadSession.flightDateTime == null) {
      patchSession(sessionId, {
        errorMessage: "Enter a flight date before uploading",
      });
      return;
    }

    if (orthoUploadSession.selectedSiteId == null) {
      patchSession(sessionId, {
        errorMessage: "Select a site before uploading",
      });
      return;
    }

    patchSession(sessionId, { errorMessage: null });

    try {
      await uploadFiles(
        sessionId,
        orthoUploadSession.selectedFiles,
        locationUuid,
        orthoUploadSession.flightDateTime,
        orthoUploadSession.selectedSiteId,
        orthoUploadSession.orthoId,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? `Failed to upload photos: ${error.message}`
          : "Failed to upload photos";
      patchSession(sessionId, { errorMessage: message });
    } finally {
      patchSession(sessionId, { selectedFiles: null });
      // Tasks were just marked completed/failed as a side effect of the upload; refetch
      // so the workflows reflect that instead of the pending status they were created with.
      await queryClient.invalidateQueries({
        queryKey: getGetAllTasksQueryKey(rawImageUploadTasksParams),
      });
    }
  }, [
    activeOrthoUploadSession,
    locationUuid,
    queryClient,
    uploadFiles,
    patchSession,
  ]);

  /** Resumes a folder's pending uploads using freshly reselected files (see `useOrthoUploader.resumeUploads`). */
  const resumeOrthoFolder = useCallback(
    async (tasks: TaskResponse[], files: File[]) => {
      const orthoUploadSession = activeOrthoUploadSession;
      if (orthoUploadSession == null) {
        return;
      }
      const sessionId = orthoUploadSession.id;

      if (orthoUploadSession.selectedSiteId == null) {
        patchSession(sessionId, {
          errorMessage: "Select a site before uploading",
        });
        return;
      }

      patchSession(sessionId, { errorMessage: null });

      try {
        await resumeUploads(sessionId, files, tasks);
      } catch (error) {
        const message =
          error instanceof Error
            ? `Failed to resume uploads: ${error.message}`
            : "Failed to resume uploads";
        patchSession(sessionId, { errorMessage: message });
      } finally {
        await queryClient.invalidateQueries({
          queryKey: getGetAllTasksQueryKey(rawImageUploadTasksParams),
        });
      }
    },
    [activeOrthoUploadSession, queryClient, resumeUploads, patchSession],
  );

  /** Stages files for upload on the active session, surfacing any staging error via its `errorMessage`. */
  const addFiles = useCallback(
    (files: File[]) => {
      const sessionId = activeOrthoUploadSessionId;
      if (sessionId == null) {
        return;
      }
      try {
        addUppyFiles(sessionId, files);
      } catch (error) {
        patchSession(sessionId, {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [activeOrthoUploadSessionId, addUppyFiles, patchSession],
  );

  const value = useMemo<OrthoWorkflowContextValue>(
    () => ({
      workflows,
      byKey,
      isPending:
        isLocationSelected && (isUploadTasksPending || isCreationTasksPending),
      locationUuid,
      upload: {
        activeSession: activeOrthoUploadSession,
        isAnyUploadInProgress,
        addFiles,
        analyzeFolder: analyzeOrthoFolder,
        clearActiveSession,
        resumeFolder: resumeOrthoFolder,
        setErrorMessage: setOrthoErrorMessage,
        setUploadStatus,
        setSelectedSiteId,
        setFlightDateTime,
        startNewSession,
        startResumeSession,
        upload: uploadOrthoFolderFile,
      },
      metadata: { knownOrthoMeta, loadOrthoMeta, rememberOrthoBoundary },
      actions,
      isActionPending,
    }),
    [
      actions,
      activeOrthoUploadSession,
      addFiles,
      analyzeOrthoFolder,
      byKey,
      clearActiveSession,
      isActionPending,
      isAnyUploadInProgress,
      isCreationTasksPending,
      isLocationSelected,
      isUploadTasksPending,
      knownOrthoMeta,
      loadOrthoMeta,
      locationUuid,
      rememberOrthoBoundary,
      resumeOrthoFolder,
      setFlightDateTime,
      setOrthoErrorMessage,
      setSelectedSiteId,
      setUploadStatus,
      startNewSession,
      startResumeSession,
      uploadOrthoFolderFile,
      workflows,
    ],
  );

  return <OrthoWorkflowContext value={value}>{children}</OrthoWorkflowContext>;
};

export default OrthoWorkflowProvider;
